import React, { useState, useCallback } from 'react';
import { Toolbar } from '@mui/x-data-grid-pro';
import {
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
} from '../CaseReview/utils/gridToolbarParts.js';
import { CustomQuickFilter } from '../CaseReview/utils/CustomQuickFilter.js';
import { usePersistedDataStore } from '../store.js';
import { useUser } from '../UserContext.js';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Menu from '@mui/material/Menu';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import OutlinedInput from '@mui/material/OutlinedInput';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import { requestTownViewTutorialReplay } from './tutorial/townViewTutorialStorage.js';
import DownloadButton from '../CaseReview/SummaryCaseTable/components/DownloadButton.js';
import DocRequestDialog from '../CaseReview/SummaryCaseTable/components/DocRequestDialog.js';
import { downloadStips } from '../CaseReview/Settlement/downloadStips.js';
import { formatCourtDateDisplayDay, UNASSIGNED_COURT_DATE } from '../../common/dateUtils.js';
import { toast } from '../../common/toast';
import StipUploadDialog from './StipUploadDialog.js';
const MENU_PROPS = {
  PaperProps: {
    style: { maxHeight: 48 * 4.5 + 8, width: 150 },
  },
};

const ICON_SX = {
  p: 0.75,
  m: 0,
  width: 32,
  height: 32,
  borderRadius: '4px',
  lineHeight: 1,
};

const COMPACT_SELECT_SX = {
  '& .MuiInputBase-root': { height: 32 },
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    height: 32,
    minHeight: '32px !important',
    py: 0,
    boxSizing: 'border-box',
  },
  '& .MuiInputLabel-root': { lineHeight: 1 },
  '& .MuiInputLabel-root.MuiInputLabel-shrink': {
    transform: 'translate(14px, -9px) scale(0.75)',
  },
};

function TownViewToolbar({
  rows,
  selectedRows,
  statusFilter,
  setStatusFilter,
  townCourtDateKeys,
  townCourtDateFilterKey,
  hasUnassignedCourtDate,
  onTownCourtDateFilterChange,
  caseData,
  fileName,
  keys,
  villageMode,
  avToggleActive,
  setAvToggleActive,
  isDataLoading,
  columnLayout,
}) {
  const getNegotiationObj = usePersistedDataStore((state) => state.negotiationObj);
  const { canPreviewTownView, setTownView } = useUser();
  const courtDate = getNegotiationObj?.CourtDate === 'All' ? null : getNegotiationObj?.CourtDate;
  const statusOptions = [
    { value: 'BAR', label: 'BAR' },
    { value: 'SCAR', label: 'SCAR' },
    { value: 'Art7', label: 'Article 7' },
    { value: 'Settled', label: 'Settled' },
  ];

  const [actionsMenuAnchor, setActionsMenuAnchor] = useState(null);
  const [stipUploadDialogOpen, setStipUploadDialogOpen] = useState(false);
  const [jurisdictionalDialogOpen, setJurisdictionalDialogOpen] = useState(false);
  const [downloadingStips, setDownloadingStips] = useState(false);
  const stipsBusy = downloadingStips;

  const handleActionsMenuClose = () => setActionsMenuAnchor(null);
  const settledSelected = (selectedRows || []).some((row) => row._isSettled || row.statusDisplay === 'Settled');

  const handleFlagJurisdictional = useCallback(() => {
    handleActionsMenuClose();
    if (settledSelected) return;
    if (!selectedRows?.length) {
      toast.error('Select at least one case');
      return;
    }
    setJurisdictionalDialogOpen(true);
  }, [selectedRows, settledSelected]);

  const handleDownloadStipulations = useCallback(async () => {
    handleActionsMenuClose();
    setDownloadingStips(true);
    try {
      await downloadStips({ rows, selectedRows });
    } finally {
      setDownloadingStips(false);
    }
  }, [selectedRows, rows, getNegotiationObj]);

  const handleUploadSignedStipulations = useCallback(() => {
    handleActionsMenuClose();
    setStipUploadDialogOpen(true);
  }, []);

  return (
    <Toolbar
      data-town-tutorial="toolbar"
      sx={{ padding: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
    >
      <div
        data-town-tutorial="toolbar-filters"
        style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '0 0 auto', width: 'max-content' }}
      >
        <GridToolbarColumnsButton columnLayout={columnLayout} />
        <GridToolbarFilterButton />

        <FormControl size="small" sx={{ minWidth: 150, '& .MuiInputBase-root': { height: 32 } }}>
          <InputLabel
            id="town-status-label"
            sx={{ bgcolor: 'transparent !important', zIndex: '1 !important', pointerEvents: 'none' }}
          >
            Case Status
          </InputLabel>
          <Select
            labelId="town-status-label"
            id="town-status-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            input={<OutlinedInput label="Case status" />}
            renderValue={(selected) => {
              const found = statusOptions.find((opt) => opt.value === selected);
              return found ? found.label : selected;
            }}
            MenuProps={MENU_PROPS}
          >
            {statusOptions.map((option) => (
              <MenuItem key={option.value} value={option.value} sx={{ py: 0 }}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 140, ...COMPACT_SELECT_SX }}>
          <InputLabel
            id="town-court-date-label"
            shrink
            sx={{ bgcolor: 'transparent !important', zIndex: '1 !important', pointerEvents: 'none' }}
          >
            Court date
          </InputLabel>
          <Select
            labelId="town-court-date-label"
            id="town-court-date-select"
            label="Court date"
            displayEmpty
            value={townCourtDateFilterKey ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              onTownCourtDateFilterChange?.(v === '' ? null : v);
            }}
            renderValue={(selected) => {
              if (!selected) return 'All dates';
              if (selected === UNASSIGNED_COURT_DATE) return 'Unassigned Date';
              return formatCourtDateDisplayDay(selected);
            }}
            MenuProps={MENU_PROPS}
          >
            <MenuItem value="" sx={{ py: 0 }}>
              All dates
            </MenuItem>
            {hasUnassignedCourtDate && (
              <MenuItem value={UNASSIGNED_COURT_DATE} sx={{ py: 0 }}>
                Unassigned Date
              </MenuItem>
            )}
            {(townCourtDateKeys || []).map((key) => (
              <MenuItem key={key} value={key} sx={{ py: 0 }}>
                {formatCourtDateDisplayDay(key)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <CustomQuickFilter />
      </div>

      <div data-town-tutorial="toolbar-actions" style={{ display: 'flex', alignItems: 'center' }}>
        <Tooltip title="Replay tutorial" placement="bottom" disableInteractive>
          <span style={{ display: 'inline-flex' }}>
            <IconButton
              data-town-tutorial="replay"
              aria-label="Replay tutorial"
              onClick={requestTownViewTutorialReplay}
              sx={{
                ...ICON_SX,
                '&:hover': { backgroundColor: 'rgba(102, 102, 102, 0.08)' },
              }}
            >
              <HelpOutlineIcon sx={{ fontSize: 20, color: '#666' }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip
          title={avToggleActive ? 'AV Mode Active - Click to disable' : 'Assessed Value Mode'}
          placement="bottom"
          disableInteractive
        >
          <span style={{ display: 'inline-flex' }}>
            <IconButton
              disabled={isDataLoading || stipsBusy}
              onClick={() => setAvToggleActive(!avToggleActive)}
              sx={{
                ...ICON_SX,
                '&:hover': {
                  backgroundColor:
                    isDataLoading || stipsBusy
                      ? 'transparent'
                      : avToggleActive
                        ? 'rgba(25, 118, 210, 0.08)'
                        : 'rgba(102, 102, 102, 0.08)',
                },
                '&.Mui-disabled': { backgroundColor: 'transparent !important' },
              }}
            >
              <span
                style={{
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  lineHeight: 1,
                  color:
                    isDataLoading || stipsBusy ? '#ccc' : avToggleActive ? '#1976d2' : '#666',
                }}
              >
                AV
              </span>
            </IconButton>
          </span>
        </Tooltip>

        <DownloadButton
          caseData={caseData}
          fileName={fileName}
          selectedRows={selectedRows}
          townView
          villageMode={villageMode}
          iconOnly
          statusFilter={statusFilter}
          activeStage="all"
          rows={rows}
          isDataLoading={isDataLoading}
          courtDate={courtDate}
          avToggleActive={avToggleActive}
        />

        <Button
          variant="contained"
          size="small"
          disabled={stipsBusy || isDataLoading}
          onClick={(e) => {
            if (stipsBusy) return;
            setActionsMenuAnchor(e.currentTarget);
          }}
          endIcon={stipsBusy ? <CircularProgress size={16} color="inherit" /> : <ArrowDropDownIcon />}
          sx={{
            ml: 1,
            px: 2,
            py: 0.5,
            fontSize: '0.75rem',
            textTransform: 'none',
          }}
        >
          {stipsBusy ? 'Downloading' : 'Actions'}
        </Button>
        <Menu
          anchorEl={actionsMenuAnchor}
          open={Boolean(actionsMenuAnchor) && !stipsBusy}
          onClose={handleActionsMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <Tooltip title={settledSelected ? 'Cannot flag issue on settled cases' : ''} placement="top">
            <span style={{ display: 'block' }}>
              <MenuItem
                onClick={handleFlagJurisdictional}
                disabled={stipsBusy || isDataLoading || settledSelected}
                sx={settledSelected ? { '&.Mui-disabled': { pointerEvents: 'auto' } } : undefined}
              >
                <ListItemText sx={{ flexGrow: 1 }}>Flag Issue</ListItemText>
                <ListItemIcon sx={{ minWidth: 'auto', ml: 2 }}>
                  <ReportProblemOutlinedIcon fontSize="small" />
                </ListItemIcon>
              </MenuItem>
            </span>
          </Tooltip>
          <MenuItem onClick={handleDownloadStipulations} disabled={stipsBusy || isDataLoading}>
            <ListItemText sx={{ flexGrow: 1 }}>Download Stips</ListItemText>
            <ListItemIcon sx={{ minWidth: 'auto', ml: 2 }}>
              <FileDownloadIcon fontSize="small" />
            </ListItemIcon>
          </MenuItem>
          <MenuItem onClick={handleUploadSignedStipulations} disabled={stipsBusy || isDataLoading}>
            <ListItemText sx={{ flexGrow: 1 }}>Upload Signed Stips</ListItemText>
            <ListItemIcon sx={{ minWidth: 'auto', ml: 2 }}>
              <UploadFileIcon fontSize="small" />
            </ListItemIcon>
          </MenuItem>
        </Menu>

        <DocRequestDialog
          open={jurisdictionalDialogOpen}
          onClose={() => setJurisdictionalDialogOpen(false)}
          rows={selectedRows || []}
          negotiationObj={getNegotiationObj}
          townView
        />

        <StipUploadDialog
          open={stipUploadDialogOpen}
          onClose={() => setStipUploadDialogOpen(false)}
        />

        {canPreviewTownView && (
          <Button variant="outlined" color="secondary" size="small" onClick={() => setTownView(false)} sx={{ ml: 1, px: 2, py: 0.5, fontSize: '0.75rem', textTransform: 'none' }}>
            Exit Town View
          </Button>
        )}
      </div>
    </Toolbar>
  );
}

export default TownViewToolbar;
